import { computed, runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { TCampaignGroup } from '@feathr/blackbox';
import { Button, Drawer } from '@feathr/components';
import { useReactionEffect } from '@feathr/hooks';

import CampaignsFilters from './CampaignsFilters';
import { defaultDateMatchMode } from './CampaignsFilters/CampaignDateMatchSelect.utils';
import type { IFilters } from './CampaignsFilters/CampaignsFilters';

interface IProps {
  campaignGroup: TCampaignGroup;
  close: () => void;
  filters: IFilters;
  isOpen: boolean;
  onChange: (filters: IFilters) => void;
}

function CampaignsFiltersDrawer({
  campaignGroup,
  close,
  filters,
  isOpen,
  onChange,
}: Readonly<IProps>): JSX.Element {
  const { eventId: projectId } = useParams<{ eventId?: string }>();
  const { t } = useTranslation();

  const drawerFilters = useLocalObservable<IFilters>(() => ({
    channels: filters.channels,
    date_end: filters.date_end,
    date_match: filters.date_match,
    date_start: filters.date_start,
    event__in: filters.event__in,
    participants: filters.participants,
    partner: filters.partner,
    state__in: filters.state__in,
    types: filters.types,
  }));

  function setDrawerFilters(newFilters: IFilters): void {
    runInAction(() => {
      drawerFilters.channels = newFilters.channels;
      drawerFilters.date_end = newFilters.date_end;
      drawerFilters.date_match = newFilters.date_match;
      drawerFilters.date_start = newFilters.date_start;
      drawerFilters.event__in = newFilters.event__in;
      drawerFilters.participants = newFilters.participants;
      drawerFilters.partner = newFilters.partner;
      drawerFilters.state__in = newFilters.state__in;
      drawerFilters.types = newFilters.types;
    });
  }

  const filterCount = computed(() => {
    /*
     * Count number of items in each filter and return the total count. `date_match` is
     * deliberately excluded: it modifies how the start/end dates are compared rather
     * than narrowing results on its own, so counting it would show a filter as applied
     * when no dates have been picked.
     *
     * `event__in` is only counted on the global route. Inside a project it is seeded
     * from the URL and the Projects select is hidden, so it describes where the user is
     * rather than a filter they chose, and counting it would report a filter they can
     * neither see nor clear.
     */
    const countedKeys = new Set([
      'channels',
      'date_end',
      'date_start',
      'participants',
      'partner',
      'state__in',
      'types',
      ...(projectId ? [] : ['event__in']),
    ]);

    return Object.entries(drawerFilters)
      .filter(([key]) => countedKeys.has(key))
      .reduce(
        (count, [, value]) => count + (Array.isArray(value) ? value.length : value ? 1 : 0),
        0,
      );
  });

  useReactionEffect(
    () => filters,
    (filters) => {
      setDrawerFilters(filters);
    },
    {},
    [filters],
  );

  function handleClearFilters(): void {
    setDrawerFilters({
      channels: [],
      date_end: undefined,
      date_match: defaultDateMatchMode,
      date_start: undefined,
      event__in: projectId ? [projectId] : [],
      participants: undefined,
      partner: undefined,
      state__in: [],
      types: [],
    });
  }

  function handleApplyFilters(): void {
    onChange(drawerFilters);
    close();
  }

  function handleCloseDrawer(): void {
    // When closing without applying, we want to reset the drawer filters to the current filters
    setDrawerFilters(filters);
    close();
  }

  return (
    <Drawer
      isOpen={isOpen}
      name={'filters-drawer'}
      onClose={handleCloseDrawer}
      title={t('Filters')}
    >
      <Drawer.Content addVerticalGap={true}>
        <CampaignsFilters
          campaignGroup={campaignGroup}
          context={projectId ? 'project' : 'global'}
          filters={drawerFilters}
          onChange={setDrawerFilters}
        />
      </Drawer.Content>
      <Drawer.Actions>
        <Button isFullWidth={true} key={'clear'} onClick={handleClearFilters}>
          {t('Clear ({{count}})', { count: filterCount.get() })}
        </Button>
        <Button isFullWidth={true} key={'apply'} onClick={handleApplyFilters} type={'primary'}>
          {t('Apply')}
        </Button>
      </Drawer.Actions>
    </Drawer>
  );
}

export default observer(CampaignsFiltersDrawer);
